import { supportsSelection } from "./workflowParticipants";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import ConfirmationDialog from "./ConfirmationDialog";

function AudiencePicker({ label, searchItems, selected, onChange, disabled }) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestGeneration = useRef(0);
  useEffect(() => {
    const generation = ++requestGeneration.current;
    if (disabled) return undefined;
    // Invalidate the old cursor immediately, including during the debounce.
    setItems([]);
    setCursor(null);
    setMore(false);
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const result = await searchItems(search, null);
        if (generation === requestGeneration.current) {
          setItems(result.items);
          setCursor(result.cursor);
          setMore(result.more);
        }
      } catch (err) {
        if (generation === requestGeneration.current) setError(err.message);
      } finally {
        if (generation === requestGeneration.current) setLoading(false);
      }
    }, 300);
    return () => {
      requestGeneration.current += 1;
      clearTimeout(timer);
    };
  }, [search, searchItems, disabled]);
  const loadMore = async () => {
    const generation = requestGeneration.current;
    setLoading(true);
    try {
      const result = await searchItems(search, cursor);
      if (generation !== requestGeneration.current) return;
      setItems((current) => [...current, ...result.items]);
      setCursor(result.cursor);
      setMore(result.more);
    } catch (err) {
      if (generation === requestGeneration.current) setError(err.message);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  };
  return (
    <Stack spacing={1}>
      <Autocomplete
        multiple
        options={[
          ...selected,
          ...items.filter(
            (item) => !selected.some((value) => value.id === item.id),
          ),
        ]}
        value={selected}
        filterOptions={(options) => options}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(item) =>
          item.name ||
          `${item.firstName || ""} ${item.lastName || ""} (${item.email})`
        }
        disabled={disabled}
        loading={loading}
        onChange={(_, value) => onChange(value)}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") setSearch(value);
        }}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
      {more && (
        <Button disabled={disabled || loading} onClick={loadMore}>
          Load more {label.toLowerCase()}
        </Button>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

export default function CustomerEnrollment({
  api,
  workflow,
  versionId,
  onEnrolled,
  disabled,
  disabledReason,
}) {
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [count, setCount] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const schoolSelection = supportsSelection(workflow, "school");
  const searchUsers = React.useCallback(
    async (search, after) => {
      const result = await api.searchEligibleUsers({
        workflowId: workflow.id,
        search,
        versionId,
        first: 25,
        after,
      });
      return {
        items: (result.edges || []).map((edge) => edge.node),
        cursor: result.pageInfo?.endCursor,
        more: result.pageInfo?.hasNextPage,
      };
    },
    [api, workflow.id, versionId],
  );
  const searchSchools = React.useCallback(
    async (search, offset) => {
      const result = await api.searchEligibleSchools({
        workflowId: workflow.id,
        versionId,
        search,
        offset: offset || 0,
      });
      return {
        items: result.items,
        cursor: (offset || 0) + result.items.length,
        more: result.hasMore,
      };
    },
    [api, workflow.id, versionId],
  );
  useEffect(() => {
    let active = true;
    setCount(null);
    setError("");
    if (!users.length && !schools.length) return undefined;
    api
      .previewAudience({
        workflowId: workflow.id,
        versionId,
        userIds: users.map((item) => item.id),
        schoolIds: schools.map((item) => item.id),
      })
      .then((result) => {
        if (active) setCount(result);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [api, workflow.id, versionId, users, schools]);
  const enroll = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { result } = await api.enrollAudience({
        workflowId: workflow.id,
        versionId,
        userIds: users.map((item) => item.id),
        schoolIds: schools.map((item) => item.id),
      });
      enqueueSnackbar(
        `Enrolled ${result.enrolled}; previously enrolled ${result.already_enrolled}; ineligible ${result.ineligible}; missing ${result.missing}; failed ${result.failed}.`,
        { variant: result.failed ? "warning" : "success" },
      );
      setUsers([]);
      setSchools([]);
      onEnrolled?.();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Enroll existing users</Typography>
        <Typography variant="body2">
          Select eligible staff users
          {schoolSelection ? " or schools to include their eligible staff" : ""}
          . Students are excluded. Each user is enrolled once per selection.
        </Typography>
        {disabled && <Alert severity="info">{disabledReason}</Alert>}
        <AudiencePicker
          label="Eligible users"
          searchItems={searchUsers}
          selected={users}
          onChange={setUsers}
          disabled={disabled || busy}
        />
        {schoolSelection && (
          <AudiencePicker
            label="Schools"
            searchItems={searchSchools}
            selected={schools}
            onChange={setSchools}
            disabled={disabled || busy}
          />
        )}
        {count !== null && (
          <Typography>{count} eligible users selected</Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          variant="contained"
          disabled={disabled || busy || !count}
          onClick={() => setConfirm(true)}
        >
          Enroll
        </Button>
      </Stack>
      <ConfirmationDialog
        open={confirm}
        title="Enroll selected users?"
        message={`Enroll ${count || 0} eligible users in ${workflow.name}, version ${workflow.versions?.find((item) => item.id === versionId)?.versionNumber || "selected"}? Eligibility is checked again when enrolling.`}
        confirmLabel="Enroll"
        onCancel={() => setConfirm(false)}
        onConfirm={enroll}
      />
    </Paper>
  );
}
